import { useEffect } from 'react';
import type { RefObject } from 'react';

interface UseOutsideSaveOptions {
    isActive: boolean;
    disabled?: boolean;
    containerRef: RefObject<HTMLElement | null>;
    portalRefs?: RefObject<HTMLElement | null>[];
    currentValue: string;
    editedValue: string;
    onSave: (value: string) => void;
    onClose: () => void;
    normalize?: (value: string) => string;
}

export const useOutsideSave = ({
    isActive,
    disabled,
    containerRef,
    portalRefs,
    currentValue,
    editedValue,
    onSave,
    onClose,
    normalize,
}: UseOutsideSaveOptions) => {
    useEffect(() => {
        if (!isActive || disabled) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null;
            const container = containerRef.current;

            if (!container || !target) return;
            if (container.contains(target)) return;
            if (portalRefs?.some((ref) => ref.current?.contains(target))) return;
            // Radix renders overlays (the style dropdown, the link popover) into
            // document.body, so they're outside `container` by construction. They
            // belong to the editor, not to the page behind it — closing on their
            // pointerdown would tear the editor down before the item is selected.
            if (
                target instanceof Element &&
                target.closest('[data-radix-popper-content-wrapper]')
            ) {
                return;
            }

            const normalizeValue = normalize || ((value: string) => value);
            const normalizedCurrent = normalizeValue(currentValue);
            const normalizedEdited = normalizeValue(editedValue);

            if (normalizedEdited !== normalizedCurrent) {
                onSave(normalizedEdited);
            }

            onClose();
        };

        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [
        isActive,
        disabled,
        containerRef,
        portalRefs,
        currentValue,
        editedValue,
        onSave,
        onClose,
        normalize,
    ]);
};
